import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Box,
  Button,
  Grid,
  Loader,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useGetCompanyNodesIdAndNameOnlyQuery } from "@js/apps/admin/api";
import type { CompanyNodeWithId } from "@js/apps/admin/types";
import { openConfirmationClientInviteModal } from "@js/apps/auth/components/confirmation-client-invite-modal";
import { ModalInstance } from "@js/components/modal";
import {
  RhfReferralLinksSearchComboboxField,
  RhfSelect,
  RhfTextField,
} from "@js/rhf/fields";
import { mapServerErrors } from "@js/rhf/utils";
import { isErrorWithDataAndStatus } from "@js/types/errors";
import { enumToOptions } from "@js/utils";

import type { CreateEmployerInvitationPayload } from "../../api";
import { useCreateEmployerInvitationMutation } from "../../api";

import styles from "./styles.module.scss";

const MSA_CLIENT_OPTIONS = enumToOptions(ENUMS.MSAClientTypeLabels);

export const openCreateNewEmployerModal = () =>
  ModalInstance.open({
    closeButton: true,
    keepOnBackdropClick: true,
    disableEscapeKeyDown: true,
    className: styles.modal,
    disablePortal: true,
    children: <CreateNewEmployerModalContent onClose={ModalInstance.close} />,
  });

const formSchema = z.object({
  first_name: z.string().min(1, { message: "Required." }),
  last_name: z.string().min(1, { message: "Required." }),
  email: z
    .string()
    .min(1, { message: "Required." })
    .email("Please enter a valid email address."),
  organization_name: z.string().min(1, { message: "Required." }),
  company_node: z.number().min(1, { message: "Required." }),
  referral_link: z
    .number()
    .nullish()
    .transform((val) => (!!val ? val : undefined)),
  msa_client_type: z.nativeEnum(ENUMS.MSAClientType, {
    required_error: "Required.",
  }),
});

type CreateNewEmployerFormValues = z.infer<typeof formSchema>;

type CreateNewEmployerProps = {
  onClose: () => void;
};

const defaultValues: Partial<CreateNewEmployerFormValues> = {
  msa_client_type: ENUMS.MSAClientType.MSA_CLIENT,
};

export const CreateNewEmployerModalContent = ({
  onClose,
}: CreateNewEmployerProps) => {
  const { data: companyNodes } = useGetCompanyNodesIdAndNameOnlyQuery({
    is_active: true,
  });

  return (
    <Box>
      <Typography component="h2" variant="title" size="small" sx={{ mb: 4 }}>
        Create a new client
      </Typography>
      {!companyNodes ? (
        <Loader centered />
      ) : (
        <CreateNewEmployerForm onClose={onClose} companyNodes={companyNodes} />
      )}
    </Box>
  );
};

type CreateNewEmployerFormProps = CreateNewEmployerProps & {
  companyNodes: CompanyNodeWithId[];
};

const CreateNewEmployerForm = ({
  onClose,
  companyNodes,
}: CreateNewEmployerFormProps) => {
  const [createInvitationMutation] = useCreateEmployerInvitationMutation();
  const createEmployerInvitation = (data: CreateEmployerInvitationPayload) => {
    return createInvitationMutation(data).unwrap();
  };

  const companyNodeOptions = useMemo(() => {
    return companyNodes.map((node) => ({
      label: node.name,
      value: node.id,
    }));
  }, [companyNodes]);

  const initialValues = useMemo(() => {
    if (companyNodeOptions.length !== 1) {
      return defaultValues;
    }

    const singleCompanyNodeOption = companyNodeOptions[0];
    const values: Partial<CreateNewEmployerFormValues> = {
      ...defaultValues,
      company_node: singleCompanyNodeOption.value,
    };

    return values;
  }, [companyNodeOptions]);

  const { handleSubmit, control, formState, setError } =
    useForm<CreateNewEmployerFormValues>({
      mode: "onTouched",
      resolver: zodResolver(formSchema),
      defaultValues: initialValues,
    });

  const onSubmit = async (data: CreateNewEmployerFormValues) => {
    try {
      await createEmployerInvitation({ ...data });
      onClose();
      openConfirmationClientInviteModal(data);
    } catch (error) {
      if (isErrorWithDataAndStatus<CreateNewEmployerFormValues>(error)) {
        const mappedErrors = mapServerErrors(error.data);
        mappedErrors.forEach((err) => {
          setError(err.name as keyof CreateNewEmployerFormValues, err.error);
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfTextField
            control={control}
            id="first_name"
            name="first_name"
            label="First name*"
            placeholder="Enter their first name"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfTextField
            control={control}
            id="last_name"
            name="last_name"
            label="Last name*"
            placeholder="Enter their last name"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfTextField
            control={control}
            id="email"
            name="email"
            label="Email*"
            placeholder="Enter their work email address"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfTextField
            control={control}
            id="organization_name"
            name="organization_name"
            label="Organization*"
            placeholder="Enter their organization"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfSelect
            control={control}
            id="company_node"
            name="company_node"
            label="Node*"
            options={companyNodeOptions}
            placeholder="Select node assigned to client"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfSelect
            control={control}
            name="msa_client_type"
            label="Client type*"
            id="msa-client-type"
            options={MSA_CLIENT_OPTIONS}
            placeholder="Select client type"
          />
        </Grid>
        <Grid
          size={{
            sm: 6,
            xs: 12,
          }}
        >
          <RhfReferralLinksSearchComboboxField
            control={control}
            id="referral_link"
            name="referral_link"
            placeholder="Select referral link"
          />
        </Grid>

        <Grid
          size={{
            sm: 9,
            xs: 12,
          }}
        >
          <Typography component="p" variant="paragraph" size="small">
            After you click “Create client”, the client will receive an email to
            activate their account and create their own password.
          </Typography>
        </Grid>
        <Grid
          size={{
            sm: 3,
            xs: 12,
          }}
          sx={{ justifyContent: "flex-end", display: "flex" }}
        >
          <Button
            variant="positive"
            shape="squared"
            size="medium"
            type="submit"
            disabled={!formState.isValid}
          >
            Create client
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
